import { format, isValid, parseISO } from "date-fns";

import { FilterDefinition } from "@/components/FilterBar";

import { OFFER_TYPES } from "../../constants";
import { formatNumber } from "../../utils";
import {
  deliveryCostAmount,
  demandDeliveryCost,
  latestDemandDeliveryCost,
  demandTermsOf,
  effectiveDeliveryCost,
  isDemandLine,
  type DeliveryCostValue,
} from "../../utils/delivery-cost";
import { isFilterSet, toFilterString } from "../../utils/filters";
import { UserGroup } from "../../utils/roles";
import { OFFER_QUICK_FILTERS, type OfferQuickFilterId } from "../RepresentativeHome/quick-filters";
import { NegotiationProposal } from "./components/OfferCommentsModal/NegotiatedDetailsCard/offer-negotiation-proposal";
import { DemandTerms, Offer, OfferStatus } from "./components/OfferCard/interfaces";
import {
  DeliveryLocationLabels,
  DeliveryLookups,
  OfferFilterKey,
  OfferFilterValues,
  OfferActionTab,
  OfferListParams,
  OfferTab,
} from "./types";

export const OFFERS_PAGE_SIZE = 10;

// Latest offer status updates first ("Showing latest offer status updates first").
export const OFFERS_DEFAULT_ORDERING = "-last_update_datetime";

export const DEFAULT_OFFER_FILTERS: OfferFilterValues = {
  status: "",
  commodity_id: "",
  quality: "",
  aggregator_id: "",
  location_id: "",
  tab: "all",
};

const ACTION_TAB_STATUSES: Record<OfferActionTab, OfferQuickFilterId[]> = {
  aggregatorAction: [OfferStatus.New, OfferStatus.Negotiating, OfferStatus.ExchangeDetailsDisputed],
  buyerAction: [
    OfferStatus.Negotiating,
    OfferStatus.ExchangeDetailsUpdated,
    OfferStatus.ExchangePaymentPending,
  ],
};

export const tabStatuses = (tab: OfferTab): OfferQuickFilterId[] =>
  tab === "aggregatorAction" || tab === "buyerAction"
    ? ACTION_TAB_STATUSES[tab]
    : OFFER_QUICK_FILTERS.filter(filter => tab === "all" || filter.group === tab).map(
        filter => filter.id
      );

/** Seeds the filter bar from router state, e.g. a status pushed by the dashboard quick filters. */
export const buildInitialFilters = (state: unknown): OfferFilterValues => {
  const s = (state ?? {}) as Partial<Record<keyof OfferFilterValues, unknown>>;
  const status = toFilterString(s.status);
  return {
    status,
    commodity_id: toFilterString(s.commodity_id),
    quality: toFilterString(s.quality),
    aggregator_id: toFilterString(s.aggregator_id),
    location_id: toFilterString(s.location_id),
    tab: OFFER_QUICK_FILTERS.find(filter => filter.id === status)?.group ?? "all",
  };
};

const statusParam = ({ status, tab }: OfferFilterValues) => {
  if (isFilterSet(status)) {
    return { status };
  }
  return tab === "all" ? {} : { status: tabStatuses(tab) };
};

export const buildOfferParams = (
  filters: OfferFilterValues,
  page: number,
  traderId?: string
): OfferListParams => ({
  page,
  page_size: OFFERS_PAGE_SIZE,
  ordering: OFFERS_DEFAULT_ORDERING,
  ...(traderId ? { trader_id: traderId } : {}),
  with_exchange: true,
  with_location: true,
  with_original_terms: true,
  ...statusParam(filters),
  ...(isFilterSet(filters.commodity_id) ? { buy_commodity_id: filters.commodity_id } : {}),
  ...(isFilterSet(filters.quality) ? { quality: filters.quality } : {}),
  ...(isFilterSet(filters.aggregator_id) ? { tagged_aggregator_id: filters.aggregator_id } : {}),
  ...(isFilterSet(filters.location_id) ? { location_id: filters.location_id } : {}),
});

/** "Status: Accepted, Commodity: Maize" for the filters currently narrowing the list. */
export const describeActiveFilters = (
  filters: OfferFilterValues,
  definitions: FilterDefinition<OfferFilterKey>[]
): string[] =>
  definitions
    .filter(definition => isFilterSet(filters[definition.key]))
    .map(definition => {
      const value = filters[definition.key];
      const option = definition.options.find(entry => entry.value === value);
      return `${definition.label}: ${option?.label ?? value}`;
    });

export const formatOfferDateTime = (date?: string | null): string => {
  if (!date) {
    return "";
  }
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, "d MMM yyyy, HH:mm") : "";
};

/**
 * Orders comments/proposals oldest-first. Clones first: sort() mutates in
 * place, and the source is usually live redux state — mutating it trips the
 * store's immutability check and corrupts index order between dispatches.
 */
export const sortByCreationTime = <T extends { backend_creation_time: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    new Date(a.backend_creation_time) > new Date(b.backend_creation_time) ? 1 : -1
  );

/** The proposal that supersedes the offer's own terms, if negotiations are live. */
export const getActiveProposal = (offer: Offer): NegotiationProposal | undefined =>
  offer.status === OfferStatus.Negotiating || offer.status === OfferStatus.ExchangeDetailsUpdated
    ? offer.latest_negotiation_proposal
    : undefined;

export const offerPictureUrls = (offer: Offer): string[] => [
  ...new Set(
    offer.deposit?.picture_urls ??
      (offer.orders ?? []).flatMap(order => order.deposit?.picture_urls ?? [])
  ),
];

export const demandPreviewPhotos = (offers: Offer[]) => {
  const lines = offers.map(offerPictureUrls).filter(urls => urls.length);
  return { urls: lines[0] ?? [], moreLines: Math.max(lines.length - 1, 0) };
};

export const isProposalFromTrader = (entity?: { user?: { roles?: string[] } }): boolean =>
  !!entity?.user?.roles?.includes(UserGroup.TRADER_REPR);

/** The organisation behind a proposal's author, for naming a negotiation column. */
export const proposalOrganisationName = (offer: Offer, proposal: NegotiationProposal): string =>
  isProposalFromTrader(proposal) ? offer.trader?.name : offer.tagged_aggregator?.name;

export type OfferBadgeVariant = "default" | "destructive" | "warning" | "success";

export const statusBadgeVariant = (status: OfferStatus): OfferBadgeVariant => {
  switch (status) {
    case OfferStatus.Accepted:
    case OfferStatus.ExchangeAgreed:
    case OfferStatus.ExchangeDone:
      return "success";
    case OfferStatus.Recalled:
    case OfferStatus.Rejected:
      return "destructive";
    case OfferStatus.OfferDone:
    case OfferStatus.ExchangeDetailsUpdated:
    case OfferStatus.ExchangeDetailsDisputed:
    case OfferStatus.ExchangePaymentPending:
      return "warning";
    default:
      return "default";
  }
};

export const offerStatusBadgeVariant = (offer: Offer): OfferBadgeVariant =>
  // Waiting on us only when the aggregator made the last move.
  offer.status === OfferStatus.Negotiating
    ? isProposalFromTrader(offer.latest_negotiation_proposal)
      ? "default"
      : "warning"
    : statusBadgeVariant(offer.status);

const DELIVERY_STAGE_STATUSES = [
  OfferStatus.ExchangeDetailsUpdated,
  OfferStatus.ExchangeDetailsDisputed,
  OfferStatus.ExchangeDone,
  OfferStatus.ExchangePaymentPending,
];

/** The final round carries delivery terms, not a price counter, once the exchange stage begins. */
export const isDeliveryRound = (offer: Offer, round: number, total: number): boolean =>
  round === total - 1 && (DELIVERY_STAGE_STATUSES.includes(offer.status) || !!offer.exchange);

/**
 * The recorded exchange as a final round, so the delivered terms compare against the last agreed ones.
 * The app writes every delivered stock's line with the whole delivered quantity and the goods amount
 * paid (not a unit price), so one line gives the round. A demand line's round carries the demand's
 * delivery cost only when that line's payment covered it.
 */
export const exchangeRound = (
  offer: Offer,
  proposals: NegotiationProposal[]
): NegotiationProposal | undefined => {
  const exchange = offer.exchange;
  if (!exchange) {
    return undefined;
  }

  const line = exchange.commodity_exchanges?.[0];
  const lineQuantity = Number(line?.quantity);
  const fromLine = !!line && lineQuantity > 0;
  const quantity = fromLine ? lineQuantity : Number(exchange.actual_total_quantity);
  const price = fromLine ? Number(line.price) / lineQuantity : Number(offer.price);
  const last = proposals[proposals.length - 1];
  const demandLine = isDemandLine(offer);
  const carriesDemandCost = demandLine && !!offer.includes_delivery_cost;

  return {
    uuid: "exchange",
    user: { id: 0, first_name: "", last_name: "", roles: [] },
    proposer: "aggregator",
    proposal_type: "EXCHANGE",
    text: "",
    responded: true,
    trader_market_offer: offer.id,
    minimum_quantity: quantity,
    maximum_quantity: quantity,
    price,
    delivery_cost: demandLine
      ? carriesDemandCost
        ? (offer.demand_terms?.delivery_cost ?? null)
        : null
      : last
        ? effectiveDeliveryCost(last, offer)
        : offer.delivery_cost,
    ...(demandLine ? { includes_delivery_cost: carriesDemandCost } : {}),
    planned_exchange_time: exchange.confirmed_at,
    backend_creation_time: exchange.confirmed_at,
    frontend_creation_time: exchange.confirmed_at,
    delivery_location: last ? last.delivery_location : offer.delivery_location,
    custom_delivery_location: last
      ? last.custom_delivery_location
      : offer.custom_delivery_location_details?.id,
    custom_delivery_location_type: last
      ? last.custom_delivery_location_type
      : offer.custom_delivery_location_type,
  };
};

export const offerDeliveryNoteUrl = (offer: Offer): string | null =>
  offer.delivery_note ?? offer.exchange?.delivery_note ?? null;

/** A direct offer is raised against a stock deposit, so the offer itself is round #1 against it. */
export const hasDepositRound = (offer: Offer): boolean =>
  offer.offer_type === OFFER_TYPES.DIRECT_OFFER && !!offer.deposit?.price;

export const offerAwaitingParty = (offer: Offer): "aggregator" | "you" | null => {
  if (offer.status === OfferStatus.New) {
    return "aggregator";
  }
  if (offer.status !== OfferStatus.Negotiating) {
    return null;
  }
  return isProposalFromTrader(offer.latest_negotiation_proposal) ? "aggregator" : "you";
};

export const canRecallOffer = (status: OfferStatus): boolean =>
  [OfferStatus.New, OfferStatus.Accepted].includes(status);

/** The offer needs the representative to act, so its primary button is emphasised. */
export const offerNeedsResponse = (offer: Offer): boolean =>
  offer.status === OfferStatus.ExchangeDetailsUpdated ||
  (offer.status === OfferStatus.Negotiating &&
    !isProposalFromTrader(offer.latest_negotiation_proposal));

export const offerStatusLabelKey = (status: OfferStatus): string => {
  const keys: Record<OfferStatus, string> = {
    [OfferStatus.Accepted]: "offerStatuses.accepted",
    [OfferStatus.ExchangeDone]: "offerStatuses.exchange_done",
    [OfferStatus.ExchangeAgreed]: "offerStatuses.exchange_agreed",
    [OfferStatus.New]: "offerStatuses.new",
    [OfferStatus.Recalled]: "offerStatuses.recalled",
    [OfferStatus.Rejected]: "offerStatuses.rejected",
    [OfferStatus.OfferDone]: "offerStatuses.offer_done",
    [OfferStatus.Negotiating]: "offerStatuses.negotiating",
    [OfferStatus.ExchangeDetailsUpdated]: "offerStatuses.exchange_details_updated",
    [OfferStatus.ExchangeDetailsDisputed]: "offerStatuses.exchange_details_disputed",
    [OfferStatus.ExchangePaymentPending]: "offerStatuses.exchange_payment_pending",
  };
  return keys[status] ?? "offerStatuses.new";
};

const PROPOSED_TERM_STATUSES = [
  OfferStatus.New,
  OfferStatus.Negotiating,
  OfferStatus.Rejected,
  OfferStatus.Recalled,
];

/** Offer terms are only "agreed" once the aggregator has accepted them. */
export const hasProposedTerms = (status: OfferStatus): boolean =>
  PROPOSED_TERM_STATUSES.includes(status);

export const offerTermsHeadingKey = (status: OfferStatus): string =>
  hasProposedTerms(status)
    ? "marketOfferView.details.offerDetailsProposed"
    : "marketOfferView.details.offerDetailsAgreed";

// A list row is either a standalone offer (demand = null) or a demand group
// bundling the child offers that share a demand id.
export type OfferRow =
  | { type: "offer"; offer: Offer }
  | { type: "demand"; demandId: number; offers: Offer[] };

// Groups child offers under their demand while keeping the server's ordering:
// each demand appears where its first child does, standalone offers stay inline.
export const groupOffersByDemand = (offers: Offer[]): OfferRow[] => {
  const rows: OfferRow[] = [];
  const rowByDemand = new Map<number, { type: "demand"; demandId: number; offers: Offer[] }>();

  offers.forEach(offer => {
    const demandId = offer.demand ?? null;
    if (demandId === null) {
      rows.push({ type: "offer", offer });
      return;
    }
    const existing = rowByDemand.get(demandId);
    if (existing) {
      existing.offers.push(offer);
    } else {
      const row = { type: "demand" as const, demandId, offers: [offer] };
      rowByDemand.set(demandId, row);
      rows.push(row);
    }
  });

  return rows;
};

// A demand raised from a school bid request is flagged per child offer by the
// API (`is_school_offer`, derived from the demand's source bid request). All
// children of one demand share the origin, so `.every` and `.some` agree; the
// flag is authoritative now that school offers also carry a quality grade.
export const isSchoolDemand = (offers: Offer[]): boolean =>
  offers.length > 0 && offers.every(offer => !!offer.is_school_offer);

// Least-advanced non-rejected child drives the demand rollup, mirroring the
// backend compute_rollup_status: rejected children are non-fatal, and the demand
// is "done" only once every remaining child is done.
const DEMAND_STATUS_RANK: Record<string, number> = {
  [OfferStatus.New]: 0,
  [OfferStatus.Negotiating]: 1,
  [OfferStatus.ExchangeDetailsUpdated]: 1,
  [OfferStatus.ExchangeDetailsDisputed]: 1,
  [OfferStatus.Accepted]: 2,
  [OfferStatus.OfferDone]: 3,
  [OfferStatus.ExchangeAgreed]: 4,
  [OfferStatus.ExchangePaymentPending]: 5,
  [OfferStatus.ExchangeDone]: 6,
};

const rankOf = (status: OfferStatus): number => DEMAND_STATUS_RANK[status] ?? 0;

export interface DemandRollup {
  status: OfferStatus;
  total: number;
  rejected: number;
  partiallyAccepted: boolean;
}

export const computeDemandRollup = (offers: Offer[]): DemandRollup => {
  const total = offers.length;
  const rejected = offers.filter(offer => offer.status === OfferStatus.Rejected).length;
  const active = offers.filter(offer => offer.status !== OfferStatus.Rejected);

  if (active.length === 0) {
    return { status: OfferStatus.Rejected, total, rejected, partiallyAccepted: false };
  }

  const status = active.reduce<OfferStatus>(
    (least, offer) => (rankOf(offer.status) < rankOf(least) ? offer.status : least),
    active[0].status
  );

  const anyAdvanced = active.some(offer => rankOf(offer.status) >= rankOf(OfferStatus.Accepted));
  const anyPending = active.some(
    offer => offer.status === OfferStatus.New || offer.status === OfferStatus.Negotiating
  );

  return { status, total, rejected, partiallyAccepted: anyAdvanced && anyPending };
};

export const demandStatusBadgeVariant = (status: OfferStatus): OfferBadgeVariant => {
  switch (status) {
    case OfferStatus.Accepted:
    case OfferStatus.ExchangeAgreed:
    case OfferStatus.ExchangeDone:
      return "success";
    case OfferStatus.Rejected:
    case OfferStatus.Recalled:
    case OfferStatus.ExchangeDetailsDisputed:
      return "destructive";
    case OfferStatus.OfferDone:
    case OfferStatus.ExchangeDetailsUpdated:
      return "warning";
    default:
      return "default";
  }
};

export const commentsButtonLabelKey = (offer: Offer): string => {
  switch (offer.status) {
    case OfferStatus.Recalled:
    case OfferStatus.Rejected:
      return "offerCard.seeReasons";
    case OfferStatus.Negotiating:
      return isProposalFromTrader(offer.latest_negotiation_proposal)
        ? "offerCard.viewNegotiation"
        : "offerCard.viewCounterOffer";
    case OfferStatus.ExchangeDetailsUpdated:
      return "offerCard.reviewExchangeQuantity";
    case OfferStatus.ExchangeDone:
      return "offerCard.exchangeDetails";
    default:
      return "offerCard.viewOfferDetails";
  }
};

export const formatQuantityRange = (minimum: string | number, maximum: string | number): string =>
  Number(minimum) === Number(maximum)
    ? formatNumber(minimum)
    : `${formatNumber(minimum)} - ${formatNumber(maximum)}`;

export const formatUnitPrice = (price: string | number, currency: string, unit: string): string =>
  `${currency}${formatNumber(price)} / ${unit}`;

/** Recalled/declined offers come back with their price zeroed; fall back to the last proposal. */
export const lastKnownPrice = (offer: Offer): string | number =>
  Number(offer.price) ? offer.price : (offer.latest_negotiation_proposal?.price ?? offer.price);

export const formatTotalPrice = (
  terms: Pick<Offer, "price" | "minimum_quantity" | "maximum_quantity" | "delivery_cost">,
  currency: string
): string => {
  const price = Number(terms.price);
  const minimum = Number(terms.minimum_quantity);
  const maximum = Number(terms.maximum_quantity || terms.minimum_quantity);
  const deliveryCost = deliveryCostAmount(terms.delivery_cost);
  return `${currency}${formatQuantityRange(
    price * minimum + deliveryCost,
    price * maximum + deliveryCost
  )}`;
};

export type DeliveryTermsSource = Pick<NegotiationProposal, "delivery_location"> & {
  custom_delivery_location?: number | null;
  custom_delivery_location_type?: number | null;
};

/**
 * Resolves the address lines for an offer's delivery location. A live proposal
 * overrides the offer's own location. Labels are injected so this stays
 * i18n-free and testable.
 */
export const resolveDeliveryLocation = (
  offer: Offer,
  proposal: DeliveryTermsSource | null | undefined,
  lookups: DeliveryLookups,
  labels: DeliveryLocationLabels
): string[] => {
  const deliveryLocation = proposal ? proposal.delivery_location : offer.delivery_location;

  switch (deliveryLocation) {
    case "drop_at_trader": {
      const trader = lookups.traders.find(({ id }) => id === offer.trader?.id);
      return [
        trader?.name || labels.nameUnavailable,
        trader?.physical_address || labels.addressUnavailable,
      ];
    }
    case "pick_from_aggregator": {
      const aggregator = lookups.aggregators.find(({ id }) => id === offer.tagged_aggregator?.id);
      return [
        aggregator?.name || labels.nameUnavailable,
        aggregator?.physical_address || labels.addressUnavailable,
      ];
    }
    case "custom_exchange_location": {
      const locationId = proposal
        ? proposal.custom_delivery_location
        : offer.custom_delivery_location_details?.id;
      const typeId = proposal
        ? proposal.custom_delivery_location_type
        : offer.custom_delivery_location_type;

      const location = lookups.customExchangeLocations.find(({ id }) => id === Number(locationId));
      const locationType = lookups.exchangeLocationTypes.find(({ id }) => id === Number(typeId));
      const area = location
        ? lookups.administrativeAreas.find(({ id }) => id === Number(location.location))
        : undefined;

      return [location?.name || labels.nameUnavailable, locationType?.name, area?.name].filter(
        (line): line is string => !!line
      );
    }
    default:
      return [labels.unknownDeliveryType];
  }
};

/** Progress of deposits added against a market offer's maximum quantity. */
export const getOfferProgress = (offer: Offer): { completed: number; total: number } => {
  const total = (offer.orders || []).reduce(
    (sum, order) => sum + (Number(order.deposit?.quantity) || 0),
    0
  );
  const maximum = Number(offer.maximum_quantity);
  const completed = maximum > 0 ? Math.min(100, (total / maximum) * 100) : 0;
  return { completed, total };
};

export interface TermChange {
  direction: "up" | "down";
  absolute: number;
}

/** Change of a counter-offer term against the original offer's value. */
export const compareTerm = (original: number, counter: number): TermChange | null => {
  if (!Number.isFinite(original) || !Number.isFinite(counter) || original === counter) {
    return null;
  }
  const absolute = counter - original;
  return { direction: absolute > 0 ? "up" : "down", absolute };
};

export interface OfferTerms {
  price: number;
  minimumQuantity: number;
  maximumQuantity: number;
  deliveryCost: number;
}

const toOfferTerms = (source: {
  price: string | number;
  minimum_quantity: string | number;
  maximum_quantity: string | number;
  delivery_cost?: DeliveryCostValue;
}): OfferTerms => ({
  price: Number(source.price),
  minimumQuantity: Number(source.minimum_quantity),
  maximumQuantity: Number(source.maximum_quantity || source.minimum_quantity),
  deliveryCost: deliveryCostAmount(source.delivery_cost),
});

export const offerOriginalSource = (offer: Offer): Offer => {
  const original = offer.original_terms;
  if (!original) {
    return offer;
  }
  return {
    ...offer,
    ...original,
    planned_exchange_time: original.planned_exchange_time ?? undefined,
    custom_delivery_location: original.custom_delivery_location ?? undefined,
    custom_delivery_location_type: original.custom_delivery_location_type ?? undefined,
  };
};

/** The offer's own terms, before any live counter-proposal. */
export const offerBaselineTerms = (offer: Offer): OfferTerms =>
  toOfferTerms({ ...offer, price: lastKnownPrice(offer) });

export const activeProposalTerms = (offer: Offer): NegotiationProposal | undefined => {
  const proposal = getActiveProposal(offer);
  return proposal && { ...proposal, delivery_cost: effectiveDeliveryCost(proposal, offer) };
};

/** The terms in force: a live proposal's when there is one, the offer's own otherwise. */
export const offerTerms = (offer: Offer): OfferTerms => {
  const proposal = activeProposalTerms(offer);
  return proposal ? toOfferTerms(proposal) : offerBaselineTerms(offer);
};

export const termsValue = (terms: OfferTerms): number => terms.price * terms.minimumQuantity;

export const termsTotal = (terms: OfferTerms): number => termsValue(terms) + terms.deliveryCost;

export interface TermMove {
  from: number;
  to: number;
}

export interface TermMoves {
  quantity?: TermMove;
  price?: TermMove;
  deliveryCost?: TermMove;
  value?: TermMove;
}

const termMove = (from: number, to: number): TermMove | undefined =>
  Number.isFinite(from) && Number.isFinite(to) && from !== to ? { from, to } : undefined;

/**
 * What a live proposal moves against the offer's own terms. The list payload carries only the
 * latest proposal, so the offer itself is the baseline.
 */
export const offerTermChanges = (offer: Offer): TermMoves => {
  const baseline = offerBaselineTerms(offer);
  const current = offerTerms(offer);

  return {
    quantity: termMove(baseline.minimumQuantity, current.minimumQuantity),
    price: termMove(baseline.price, current.price),
    deliveryCost: termMove(baseline.deliveryCost, current.deliveryCost),
    value: termMove(termsTotal(baseline), termsTotal(current)),
  };
};

const BRIEF_PREFIX = "representativeOffers.card.brief";

/** One-line summary of what the offer's status means for the representative. */
export const offerBriefKey = (offer: Offer): string => {
  switch (offer.status) {
    case OfferStatus.Negotiating: {
      if (isProposalFromTrader(offer.latest_negotiation_proposal)) {
        return `${BRIEF_PREFIX}.awaitingAggregator`;
      }
      const { quantity, price } = offerTermChanges(offer);
      if (quantity && price) {
        return `${BRIEF_PREFIX}.counteredBoth`;
      }
      if (quantity) {
        return `${BRIEF_PREFIX}.counteredQuantity`;
      }
      if (price) {
        return `${BRIEF_PREFIX}.counteredPrice`;
      }
      return `${BRIEF_PREFIX}.countered`;
    }
    case OfferStatus.Accepted:
      return `${BRIEF_PREFIX}.accepted`;
    case OfferStatus.OfferDone:
      return `${BRIEF_PREFIX}.readyForExchange`;
    case OfferStatus.ExchangeAgreed:
      return `${BRIEF_PREFIX}.exchangeAgreed`;
    case OfferStatus.ExchangeDetailsUpdated:
      return `${BRIEF_PREFIX}.exchangeDetailsUpdated`;
    case OfferStatus.ExchangeDetailsDisputed:
      return `${BRIEF_PREFIX}.exchangeDetailsDisputed`;
    case OfferStatus.ExchangePaymentPending:
      return `${BRIEF_PREFIX}.exchangePaymentPending`;
    case OfferStatus.ExchangeDone:
      return `${BRIEF_PREFIX}.exchangeDone`;
    case OfferStatus.Rejected:
      return `${BRIEF_PREFIX}.rejected`;
    case OfferStatus.Recalled:
      return `${BRIEF_PREFIX}.recalled`;
    default:
      return `${BRIEF_PREFIX}.new`;
  }
};

export interface DemandTotals {
  items: number;
  quantity: number;
  value: number;
  deliveryCost: number;
}

const activeOffers = (offers: Offer[]): Offer[] =>
  offers.filter(offer => offer.status !== OfferStatus.Rejected);

const OUT_STATUSES = [OfferStatus.Rejected, OfferStatus.Recalled];

/** A demand's lines still in play: declined and recalled lines are out. */
export const demandLinesInPlay = (offers: Offer[]): Offer[] =>
  offers.filter(offer => !OUT_STATUSES.includes(offer.status));

/**
 * Line count, quantity and goods value of a demand's lines in play, plus the demand's single
 * delivery cost, counted once (and dropped once no line is left in play).
 */
export const demandTotals = (offers: Offer[]): DemandTotals => {
  const totals = demandLinesInPlay(offers).reduce(
    (sum, offer) => {
      const terms = offerTerms(offer);
      return {
        items: sum.items + 1,
        quantity: sum.quantity + terms.minimumQuantity,
        value: sum.value + termsValue(terms),
      };
    },
    { items: 0, quantity: 0, value: 0 }
  );
  return {
    ...totals,
    deliveryCost: totals.items ? deliveryCostAmount(demandDeliveryCost(offers)) : 0,
  };
};

/** The demand's delivery cost as it stands (latest proposal) against the amount the buyer first offered. */
export const demandDeliveryCostMove = (terms: DemandTerms | null): TermMove | undefined =>
  terms
    ? termMove(
        deliveryCostAmount(terms.original_delivery_cost),
        deliveryCostAmount(latestDemandDeliveryCost(terms))
      )
    : undefined;

/** Demand-level moves: summed line baselines against summed live terms, plus the demand's cost. */
export const demandTermChanges = (offers: Offer[]): TermMoves => {
  const sum = (pick: (offer: Offer) => OfferTerms) =>
    demandLinesInPlay(offers).reduce(
      (totals, offer) => {
        const terms = pick(offer);
        return {
          quantity: totals.quantity + terms.minimumQuantity,
          value: totals.value + termsValue(terms),
        };
      },
      { quantity: 0, value: 0 }
    );

  const baseline = sum(offerBaselineTerms);
  const current = sum(offerTerms);
  const terms = demandTermsOf(offers);
  const originalCost = deliveryCostAmount(terms?.original_delivery_cost);
  const currentCost = deliveryCostAmount(latestDemandDeliveryCost(terms));

  return {
    quantity: termMove(baseline.quantity, current.quantity),
    deliveryCost: demandDeliveryCostMove(terms),
    value: termMove(baseline.value + originalCost, current.value + currentCost),
  };
};

const STANDING_STATUSES = [OfferStatus.New, OfferStatus.Negotiating];

/**
 * The aggregator's pending delivery-cost amount the buyer also accepts by closing this line: once a
 * demand's last standing line is closed, the backend locks the demand's cost on the aggregator's
 * pending proposal (when every line ends up declined or recalled, it is withdrawn instead).
 * Null when closing the line settles nothing.
 */
export const closingAcceptsDeliveryCost = (offer: Offer): DeliveryCostValue => {
  const terms = offer.demand_terms;
  const pending = terms?.pending_delivery_cost_proposal;
  if (
    !terms ||
    terms.delivery_cost_locked ||
    terms.standing_lines !== 1 ||
    !STANDING_STATUSES.includes(offer.status) ||
    pending?.side !== "AGGREGATOR"
  ) {
    return null;
  }
  return pending.delivery_cost;
};

/** The line a demand's single call-to-action acts on: the one needing a response, else the least advanced. */
export const rollupDriverOffer = (offers: Offer[]): Offer => {
  const pool = activeOffers(offers).length ? activeOffers(offers) : offers;
  return (
    pool.find(offerNeedsResponse) ??
    pool.reduce(
      (least, offer) => (rankOf(offer.status) < rankOf(least.status) ? offer : least),
      pool[0]
    )
  );
};

// Read-only on an offer PUT: the payment flag is the aggregator's, the demand summary is derived.
const READ_ONLY_OFFER_KEYS = ["includes_delivery_cost", "demand_terms"];

/** An offer read turned into a PUT body; a demand line's cost is the demand's, so it sends none. */
export const toOfferUpdatePayload = <T extends Offer>(offer: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(offer).filter(
      ([key]) =>
        !READ_ONLY_OFFER_KEYS.includes(key) && !(key === "delivery_cost" && isDemandLine(offer))
    )
  ) as Partial<T>;
